import { qoursesApiInstructor } from '@/api/qourses-instructor.tsx'
import Dynamic from '@/components/modals/dynamic.tsx'
import useGetInstructorCalendars from '@/hooks/instructor/useGetInstructorCalendars.tsx'
import { Button } from '@/shadcn/components/ui/button.tsx'
import { DialogDescription, DialogHeader, DialogTitle } from '@/shadcn/components/ui/dialog.tsx'
import { Input } from '@/shadcn/components/ui/input.tsx'
import { ToastVariant, sendNotification } from '@/utils.tsx'
import { ChevronRight, Cog, Copy } from 'lucide-react'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'

export default function InstructorAddMeetingsToCalendarModal() {
  const { t: translate } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL
  const [loading, setLoading] = useState(false)
  const [manualCalendarUrl, setManualCalendarUrl] = useState<string | null>(null)

  const {
    calendars,
    isLoading: isLoadingCalendars,
    isError: isErrorCalendars,
    refetch,
  } = useGetInstructorCalendars()

  const isMobile = useMediaQuery({ query: '(max-width: 750px)' })

  const handleCreateCalendar = async (
    calendarProvider: 'apple' | 'google' | 'outlook' | 'manual',
  ) => {
    setLoading(true)

    try {
      const calendar = await qoursesApiInstructor.calendar.calendarControllerCreateCalendarV1({})

      // Validate that calendar was created successfully and has a token
      if (!calendar || !calendar.token) {
        sendNotification(
          translate('modals.backofficeAddMeetingsToCalendarModal.notifications.error.title'),
          translate('modals.backofficeAddMeetingsToCalendarModal.notifications.error.subtitle'),
          ToastVariant.Error,
        )
        setLoading(false)
        return
      }

      const calendarUrl = `${apiUrl}/v1/public/calendar/${calendar.token}/${DateTime.now().plus({ years: 3 }).toISODate()}`

      if (calendarProvider === 'apple') {
        const webcalUrl = calendarUrl.replace('https://', 'webcal://')
        if (isMobile) {
          setTimeout(() => {
            window.open(`${webcalUrl}`, '_blank')
          })
        } else {
          setTimeout(() => {
            window.open(`${webcalUrl}`, 'noopener,noreferrer')
          })
        }
      }

      if (calendarProvider === 'google') {
        const webcalUrl = calendarUrl.replace('https://', 'webcal://')
        setTimeout(() => {
          window.open(`https://calendar.google.com/calendar/r?cid=${webcalUrl}`, '_blank')
        })
      }

      if (calendarProvider === 'outlook') {
        setTimeout(() => {
          window.open(
            `https://outlook.live.com/calendar/addcalendar?url=${calendarUrl}&name=Qourses`,
          )
        })
      }

      if (calendarProvider === 'manual') {
        setManualCalendarUrl(calendarUrl)
      }

      refetch()
    } catch (error) {
      console.error(error)
      sendNotification(
        translate('modals.backofficeAddMeetingsToCalendarModal.notifications.error.title'),
        translate('modals.backofficeAddMeetingsToCalendarModal.notifications.error.subtitle'),
        ToastVariant.Error,
      )
    } finally {
      setLoading(false)
    }
  }

  const handleArchiveCalendar = async (calendarId: string) => {
    try {
      await qoursesApiInstructor.calendar.calendarControllerArchiveCalendarV1(calendarId)
      refetch()
    } catch (error) {
      console.error(error)
    }
  }

  const handleCopyManualCalendarUrl = () => {
    if (manualCalendarUrl) {
      navigator.clipboard.writeText(manualCalendarUrl)
      sendNotification(
        translate('modals.backofficeAddMeetingsToCalendarModal.notifications.manualCopy.title'),
        translate('modals.backofficeAddMeetingsToCalendarModal.notifications.manualCopy.subtitle'),
        ToastVariant.Info,
      )
    }
  }

  return (
    <Dynamic.Content className="p-4 sm:p-8">
      <DialogHeader className="mb-2">
        <DialogTitle className="flex flex-col gap-y-2 sm:mt-2">
          <p className="mt-4">{translate('modals.backofficeAddMeetingsToCalendarModal.title')}</p>
        </DialogTitle>
        <DialogDescription>
          {translate('modals.backofficeAddMeetingsToCalendarModal.subtitle')}
        </DialogDescription>
      </DialogHeader>

      <Button
        variant="outline"
        className="mt-3 sm:mt-0"
        onClick={() => handleCreateCalendar('apple')}
        disabled={loading}
      >
        <img className="inline-block size-6 p-1 mr-1" src="/logos/apple.svg" alt="Apple Logo" />
        {translate('modals.backofficeAddMeetingsToCalendarModal.buttonApple')}
      </Button>
      <Button
        variant="outline"
        className="mt-3 sm:mt-0"
        onClick={() => handleCreateCalendar('google')}
        disabled={loading}
      >
        <img className="inline-block size-6 p-1 mr-1" src="/logos/google.svg" alt="Google Logo" />
        {translate('modals.backofficeAddMeetingsToCalendarModal.buttonGoogle')}
      </Button>
      <Button
        variant="outline"
        className="mt-3 mb-2 sm:mt-0 sm:mb-0"
        onClick={() => handleCreateCalendar('outlook')}
        disabled={loading}
      >
        <img
          className="inline-block size-6 p-1 mr-1"
          src="/logos/microsoft.svg"
          alt="Microsoft Logo"
        />
        {translate('modals.backofficeAddMeetingsToCalendarModal.buttonOutlook')}
      </Button>
      <div className="mt-2 border-t-2 border-dashed">
        {manualCalendarUrl ? (
          <div className="flex gap-2 mt-3 sm:mt-6">
            <Input readOnly value={manualCalendarUrl} className="flex-1 bg-white" />
            <Button variant="outline" onClick={handleCopyManualCalendarUrl}>
              <Copy className="inline-block size-4 mr-1" />
              {translate('modals.backofficeAddMeetingsToCalendarModal.buttonCopy') || 'Copy'}
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            className="flex flex-1 w-full mt-3 sm:mt-6"
            onClick={() => handleCreateCalendar('manual')}
            disabled={loading}
          >
            <Cog className="inline-block size-6 p-1 mr-1" />
            {translate('modals.backofficeAddMeetingsToCalendarModal.buttonManual')}
          </Button>
        )}
      </div>

      {calendars.length > 0 && (
        <>
          <div className="relative mt-4 mb-2">
            <div aria-hidden="true" className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-2 font-semibold text-muted-foreground">
                {translate('modals.backofficeAddMeetingsToCalendarModal.setupCalendarsTitle')}
              </span>
            </div>
          </div>
          <div className="relative bg-gray-50 p-2 ring-1 ring-gray-200 rounded-lg shadow-inner max-h-32 overflow-y-auto">
            {!isLoadingCalendars &&
              !isErrorCalendars &&
              calendars.map((calendar) => (
                <div
                  key={calendar.id}
                  className="mt-2 bg-white text-sm flex gap-x-1 p-2 border rounded-md items-center justify-between first:mt-2 last:mb-2"
                >
                  <div>
                    <p>{translate('modals.backofficeAddMeetingsToCalendarModal.lastSynced')}</p>
                    <p className="font-semibold text-xs">
                      {calendar.lastUsageDay !== null
                        ? DateTime.fromISO(calendar.lastUsageDay).toLocaleString(DateTime.DATE_FULL)
                        : translate('modals.backofficeAddMeetingsToCalendarModal.syncPending')}
                    </p>
                  </div>
                  <div
                    className="cursor-pointer text-xs underline mt-1 flex items-center"
                    onClick={() => handleArchiveCalendar(calendar.id)}
                  >
                    {translate('modals.backofficeAddMeetingsToCalendarModal.archiveCalendar')}
                    <ChevronRight className="ml-1 size-3" />
                  </div>
                </div>
              ))}
          </div>
        </>
      )}
    </Dynamic.Content>
  )
}
